"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
interface StepItem {
  number: string;
  stepName: string;
  title: string;
  description: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  details: string[];
}
const STEPS: StepItem[] = [{
  number: "01",
  stepName: "Consultation",
  title: "Initial meeting & project assessment",
  description: "We begin at your Brookhaven home to evaluate your space, discuss your functional requirements, and establish your aesthetic goals. During this collaborative walkthrough, we inspect structural conditions, review existing layouts, and discuss practical possibilities tailored to your neighborhood's architectural style.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_consultation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_consultation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_consultation.png-wvc-sizes",
    alt: "Contractor and homeowner reviewing architectural plans during an in-home consultation"
  },
  details: ["On-site spatial assessment and lifestyle review", "Identification of structural and mechanical scope", "Clear alignment on expectations and scope definition"]
}, {
  number: "02",
  stepName: "Design & Planning",
  title: "Design development & material selection",
  description: "Our team translates your vision into detailed 3D renderings and comprehensive architectural plans. You work alongside our specialists to select every finish—from cabinetry and natural stone slabs to plumbing fixtures and custom millwork—locking down every detail before any hammer strikes.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_kitchen_design_samples.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_kitchen_design_samples.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_kitchen_design_samples.webp-wvc-sizes",
    alt: "Designer reviewing custom tile, marble samples, and cabinetry finishes on a drafting table"
  },
  details: ["Detailed architectural layouts and finish schematics", "Curated showroom material appointments", "Fixed material allowances and transparent specifications"]
}, {
  number: "03",
  stepName: "Permitting",
  title: "City of Brookhaven & DeKalb County permitting",
  description: "Navigating local zoning and municipal building codes can be intricate. We prepare and submit all construction documentation directly to the City of Brookhaven Community Development Department or DeKalb County authorities, securing all necessary structural, electrical, and plumbing permits without burdening you.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/city_permitting_documentation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/city_permitting_documentation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/city_permitting_documentation.png-wvc-sizes",
    alt: "Approved municipal building permit documents and architectural drawings on a desk"
  },
  details: ["Zoning verification and setback reviews", "City of Brookhaven & DeKalb County filings", "Management of trade-specific municipal approvals"]
}, {
  number: "04",
  stepName: "Construction",
  title: "Craftsmanship & project execution",
  description: "Construction begins with clean site containment, protective floor coverings, and dedicated dust control systems. Our vetted tradespeople handle framing, mechanical rough-ins, drywall, tile installations, and millwork under the daily supervision of a dedicated on-site project manager.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/residential_renovation_construction.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/residential_renovation_construction.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/residential_renovation_construction.png-wvc-sizes",
    alt: "Skilled carpenter installing custom interior cabinetry and wood trim during renovation"
  },
  details: ["Daily site oversight by a dedicated manager", "Thorough dust barrier and floor protection protocol", "Regular milestone check-ins and open communications"]
}, {
  number: "05",
  stepName: "Completion",
  title: "Quality inspection & final walkthrough",
  description: "Before handing over your transformed space, we conduct rigorous internal quality checks, manage final municipal building and trade inspections, and complete a detailed punch list. We walk through every room with you to ensure every surface, finish, and fixture meets our standard of excellence.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/completed_interior_walkthrough.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/completed_interior_walkthrough.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/completed_interior_walkthrough.png-wvc-sizes",
    alt: "Finished modern luxury kitchen with white oak cabinets, marble island, and warm lighting"
  },
  details: ["Comprehensive multi-point quality punch list", "City final inspection sign-offs and certificates", "Appliance documentation and project closeout packet"]
}];
export default function RenovationProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  return <section id="renovationprocess" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 lg:mb-24 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Our Process
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground leading-tight">
            A structured sequence designed for precision and peace of mind
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From your living room consultation through DeKalb County permitting to the final turn of the key, our step-by-step approach ensures full clarity at every phase of your renovation.
          </p>
        </div>

        {/* Steps List */}
        <div className="space-y-16 lg:space-y-24">
          {STEPS.map((step, index) => {
          const isEven = index % 2 === 1;
          return <div key={step.number} data-index={index} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${index * 120}ms`
          }}>
                {/* Visual Media Column */}
                <div className={`lg:col-span-6 ${isEven ? "lg:order-2" : "lg:order-1"}`}>
                  <Card className="overflow-hidden border border-border shadow-md rounded-2xl bg-card">
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center justify-center font-mono font-bold text-sm bg-secondary text-secondary-foreground px-3 py-1 rounded-md shadow-xs">
                          Step {step.number}
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Content Column */}
                <div className={`lg:col-span-6 flex flex-col justify-center ${isEven ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold tracking-wider uppercase text-primary">
                      {step.stepName}
                    </span>
                    <span className="w-8 h-px bg-border" />
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground tracking-tight leading-snug mb-4">
                    {step.title}
                  </h3>

                  <p className="text-base text-muted-foreground leading-relaxed mb-6">
                    {step.description}
                  </p>

                  <Card className="border-border bg-card/60 rounded-xl py-4">
                    <CardContent className="px-5 py-0">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        What to expect
                      </h4>
                      <ul className="space-y-2.5">
                        {step.details.map((detail, detailIndex) => <li key={detailIndex} data-index={detailIndex} className="flex items-start gap-2.5 text-sm text-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{detail}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>;
        })}
        </div>

        {/* Closing Action Banner */}
        <div className="mt-20 lg:mt-28 p-8 sm:p-12 rounded-2xl bg-secondary text-secondary-foreground border border-border shadow-md">
          <div className="max-w-3xl">
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-secondary-foreground tracking-tight mb-3">
              Ready to start step one?
            </h3>
            <p className="text-secondary-foreground/80 text-base sm:text-lg mb-8 leading-relaxed">
              Schedule your in-home project consultation with our Brookhaven renovation specialists to assess your space and outline your goals.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestAnEstimatePage">
                  Request a consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent">
                <Link to="/OurProjectsPage">
                  Explore completed work
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}